import { Form, Formik, FormikConfig } from 'formik';
import { memo, useCallback, useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import * as yup from 'yup';
import { Card } from '../../../components/Card';
import { FormikInput } from '../../../components/formik/FormInput';
import { UserContext } from '../../../contexts/UserContext';
import { ToastType, useToastNotifications } from '../../../hooks/useToastNotifications';
import { useValidationTranslations } from '../../../hooks/useValidationTranslations';
import { useT } from '../../../translation/src';
import { useOrganisationUpdateMutation, useOrganisationsQuery } from '../../../types/graphqlTypes';
import { useDeviceSize } from '../../../hooks/useDeviceSize';

interface FormValues {
  name: string;
}

const useTranslations = () => {
  const companyName = useT('company name', { swc: true });
  const general = useT('general', { swc: true });
  const save = useT('save', { swc: true });
  const saved = useT('organisation saved', { swc: true });
  const saveFailed = useT('could not save the organisation', { swc: true });
  return { companyName, general, save, saved, saveFailed };
};

const OrganisationCompanyInfo_: React.FC2 = () => {
  const translations = useTranslations();
  const { organisationId = '' } = useParams();
  const validationTranslations = useValidationTranslations();
  const { showToast } = useToastNotifications();
  const user = useContext(UserContext);
  const { data: organisationsData } = useOrganisationsQuery(user ? { variables: { includeIds: [organisationId] } } : {});
  const [updateOrganisation, { loading: updateOrganisationLoading }] = useOrganisationUpdateMutation();
  const organisations = organisationsData?.organisations;
  const organisation = organisations?.find((org) => org.id === organisationId);
  const { xsDevice } = useDeviceSize();

  const initialValues = useMemo(() => ({ name: organisation?.name || '' }), [organisation]);

  const validationSchema = useMemo(() => yup.object().shape({ name: yup.string().trim().required(validationTranslations.isRequired) }), [validationTranslations.isRequired]);

  const onSubmit: FormikConfig<FormValues>['onSubmit'] = useCallback(
    async (values, helpers) => {
      let failed = false;
      await updateOrganisation({
        variables: { name: values.name.trim(), organisationId },
        onError: (error) => {
          failed = true;
          if (error.message === 'ALREADY_EXISTS') helpers.setFieldError('name', validationTranslations.alreadyExists);
          else if (error.message === 'INVALID_NAME') helpers.setFieldError('name', validationTranslations.isRequired);
          else showToast({ title: translations.saveFailed, text: error.message, type: ToastType.ERROR });
        },
      });
      if (!failed) showToast({ title: translations.saved, text: '', type: ToastType.SUCCESS });
    },
    [organisationId, showToast, translations.saveFailed, translations.saved, updateOrganisation, validationTranslations.alreadyExists, validationTranslations.isRequired],
  );

  return (
    <div className="flex space-x-4">
      <Formik<FormValues> onSubmit={onSubmit} initialValues={initialValues} validationSchema={validationSchema} enableReinitialize>
        <Form className={xsDevice ? 'w-full' : 'w-1/2'}>
          <Card title={translations.general} className="w-full" submitButtonTitle={translations.save} isSubmitting={updateOrganisationLoading}>
            <div className="flex flex-col">
              <FormikInput label={translations.companyName} name="name" autoComplete="organization" />
            </div>
          </Card>
        </Form>
      </Formik>
    </div>
  );
};

export const OrganisationCompanyInfo = memo(OrganisationCompanyInfo_);
